import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { openReadyWhatsApp, fmtDue } from "@/lib/collection";
import { whatsappUrl } from "@/lib/share";
import { BACKEND_URL } from "@/lib/backendUrl";

const API = `${BACKEND_URL}/api`;

// Shared state + actions for the "In workshop" strip and the full Workshop page
export function useWorkshopJobs(shop, onChange, onCollected) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => axios.get(`${API}/quotes/ongoing`).then(({ data }) => setItems(data)).catch(() => setItems([])).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const notified = async (qt) => {
    try {
      const { data } = await axios.post(`${API}/quotes/${qt.id}/ready-notified`);
      setItems((l) => l.map((x) => (x.id === qt.id ? { ...x, ready_notified_at: data.ready_notified_at } : x)));
    } catch (err) { console.warn("Ready stamp not saved (message already opened):", err?.response?.status || err?.message); }
  };

  const setDue = async (qt, expected_done) => {
    try {
      const { data } = await axios.patch(`${API}/workshop/quotes/${qt.id}/expected-done`, { expected_done });
      setItems((l) => l.map((x) => (x.id === qt.id ? { ...x, expected_done: data.expected_done } : x)));
      toast.success(expected_done ? `Due ${fmtDue(expected_done)}` : "Due date cleared");
    } catch (e) { toast.error("Could not save date", { description: e?.response?.data?.detail }); }
  };

  // Mark collected; WhatsApp only when explicitly asked (notify: true)
  const collected = async (qt, { notify = false } = {}) => {
    const sent = notify ? openReadyWhatsApp(qt, shop) : null;
    try {
      const { data } = await axios.patch(`${API}/quotes/${qt.id}/ongoing`, { ongoing: false });
      setItems((l) => l.filter((x) => x.id !== qt.id));
      const wa = notify ? (sent ? "WhatsApp opened for the customer" : "No phone on this quote — customer not messaged") : null;
      const invMsg = data.invoice_created ? `Invoice ${data.invoice_created} created · 已开票` : null;
      toast.success(`${qt.plate || qt.quote_no} collected · 已交车`, { description: [invMsg, wa].filter(Boolean).join(" · ") || undefined });
      onChange?.(); onCollected?.(data);
    } catch (e) { toast.error("Could not update", { description: e?.response?.data?.detail }); }
  };

  // Morning summary to the owner's WhatsApp (recipient from Prices → General; otherwise pick the chat in WhatsApp)
  const sendSummary = async () => {
    const win = window.open("about:blank", "_blank");
    try {
      const { data } = await axios.get(`${API}/workshop/summary`);
      win.location.href = whatsappUrl(shop.ownerWhatsapp, shop.countryCode, data.text);
      toast.success("Opening WhatsApp · workshop summary");
    } catch (e) { win?.close(); toast.error("Could not build summary"); }
  };

  return { items, loading, reload: load, notified, setDue, collected, sendSummary };
}
